import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button, IconButton, Loading } from "@/components/kit";
import { PageHeader, StickyBar } from "@/components/layout";
import { DetailsForm } from "@/components/project/DetailsForm";
import { MediaSection } from "@/components/project/MediaSection";
import { VideoList } from "@/components/project/VideoList";
import { useToast } from "@/components/toast";
import { api } from "@/lib/api";
import { useUploads } from "@/lib/uploads";

const COPY = {
  project: { title: "Delete this property?", message: "Its photos, videos and finished videos will be removed.", label: "Delete" },
  video: { title: "Delete this video?", message: "The finished video will be removed from this property.", label: "Delete" },
  media: { title: "Remove this item?", message: "It won't be used in future videos.", label: "Remove" },
};

export default function ProjectPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const uploads = useUploads(id);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const q = useQuery({
    queryKey: ["project", id],
    queryFn: () => api(`/projects/${id}`),
    refetchInterval: (qq) => (qq.state.data?.videos.some((v) => v.status === "queued" || v.status === "running") ? 2500 : false),
  });
  const p = q.data;

  if (!p) {
    return (
      <section data-testid="project-screen">
        <PageHeader title="Property" />
        {q.isError ? (
          <div className="flex flex-col items-start gap-4" data-testid="project-load-error">
            <p className="text-base text-card-foreground">{q.error.message}</p>
            <Link to="/projects" className="focus-ring rounded font-semibold text-primary hover:underline" data-testid="project-back-to-list-link">Back to properties</Link>
          </div>
        ) : <Loading />}
      </section>
    );
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["project", id] });
    qc.invalidateQueries({ queryKey: ["projects"] });
  };

  const reorder = async (keys) => {
    qc.setQueryData(["project", id], (old) => (old ? { ...old, media: keys.map((k) => old.media.find((m) => m.id === k)).filter(Boolean) } : old));
    try {
      await api(`/projects/${id}/media/order`, { method: "PUT", body: { media_ids: keys } });
      qc.invalidateQueries({ queryKey: ["projects"] });
    } catch (e) {
      toast(e.message, "error");
      refresh();
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      if (confirm.kind === "project") {
        await api(`/projects/${id}`, { method: "DELETE" });
        qc.removeQueries({ queryKey: ["project", id] });
        qc.invalidateQueries({ queryKey: ["projects"] });
        setConfirm(null);
        navigate("/projects", { replace: true });
        return;
      }
      await api(confirm.kind === "media" ? `/projects/${id}/media/${confirm.id}` : `/renders/${confirm.id}`, { method: "DELETE" });
      refresh();
      setConfirm(null);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setDeleting(false);
    }
  };

  const noMedia = p.media.length === 0;
  const busyUploads = uploads.items.some((u) => u.status === "queued" || u.status === "uploading");
  const copy = COPY[confirm?.kind ?? "media"];

  return (
    <section data-testid="project-screen">
      <PageHeader title={p.address} eyebrow="Property" back="/projects"
        right={<IconButton icon={Trash2} label="Delete property" tint="text-destructive" className="hover:bg-destructive/10"
          onClick={() => setConfirm({ kind: "project", id: p.id })} data-testid="delete-project-button" />} />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <aside className="lg:sticky lg:top-10 lg:col-start-2 lg:row-start-1">
          <DetailsForm p={p} onSaved={refresh} />
        </aside>
        <div className="flex min-w-0 flex-col gap-10 lg:col-start-1 lg:row-start-1">
          <MediaSection projectId={id} media={p.media} onReorder={reorder} onRemove={(mid) => setConfirm({ kind: "media", id: mid })} />
          <VideoList videos={p.videos} onDelete={(vid) => setConfirm({ kind: "video", id: vid })} />
        </div>
      </div>
      <StickyBar>
        {noMedia ? <p className="text-center text-[13px] text-muted-foreground sm:mr-auto" data-testid="create-video-disabled-hint">Add at least one photo or video</p>
          : busyUploads ? <p className="text-center text-[13px] text-muted-foreground sm:mr-auto" data-testid="uploads-in-progress-hint">Uploads in progress…</p> : null}
        <Button title="Create video" icon={Sparkles} disabled={noMedia} onClick={() => navigate(`/projects/${id}/create`)}
          className="sm:min-w-[240px]" data-testid="open-create-video-button" />
      </StickyBar>
      <ConfirmDialog open={!!confirm} testId="confirm-delete-sheet" loading={deleting} title={copy.title} message={copy.message}
        confirmLabel={copy.label} onCancel={() => setConfirm(null)} onConfirm={doDelete} />
    </section>
  );
}
